import { useEffect, useState } from "react"
import { Moon, Sun } from "lucide-react"

import { applyPalette, loadSelection, saveSelection, type ThemeSelection } from "@/lib/theme"
import { BUTTON_PALETTES, getPalette, PALETTES, type Mode } from "@/themes/palettes"

const selectClass =
  "rounded-md border border-input bg-card px-3 py-1.5 text-sm font-medium text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"

export function ThemeSwitcher() {
  const [selection, setSelection] = useState<ThemeSelection>(() => loadSelection())

  useEffect(() => {
    applyPalette(selection)
    saveSelection(selection)
  }, [selection])

  const palette = getPalette(selection.paletteId)
  const hasMode = (m: Mode) => Boolean(palette.modes[m])

  function setMode(mode: Mode) {
    setSelection((s) => ({ ...s, mode }))
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        Palette
        <select
          value={selection.paletteId}
          onChange={(e) => setSelection((s) => ({ ...s, paletteId: e.target.value }))}
          className={selectClass}
        >
          {PALETTES.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
      </label>

      <label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        Buttons
        <select
          value={selection.buttonsId ?? ""}
          onChange={(e) => setSelection((s) => ({ ...s, buttonsId: e.target.value || null }))}
          className={selectClass}
        >
          <option value="">Theme default</option>
          {BUTTON_PALETTES.map((b) => (
            <option key={b.id} value={b.id}>
              {b.label}
            </option>
          ))}
        </select>
      </label>

      <div className="inline-flex overflow-hidden rounded-md border border-input">
        <button
          type="button"
          onClick={() => setMode("light")}
          disabled={!hasMode("light")}
          aria-pressed={selection.mode === "light"}
          title="Light"
          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-40 aria-pressed:bg-primary aria-pressed:text-primary-foreground"
        >
          <Sun className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => setMode("dark")}
          disabled={!hasMode("dark")}
          aria-pressed={selection.mode === "dark"}
          title="Dark"
          className="flex items-center gap-1 border-l border-input px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-40 aria-pressed:bg-primary aria-pressed:text-primary-foreground"
        >
          <Moon className="size-4" />
        </button>
      </div>
    </div>
  )
}
